import { makeStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IAppStore } from '../../../../types/IAppStore';
import { IPremiumBonusType } from '../../../../types/ICabinetStore';
import { ReactComponent as BooksIcon } from '../../../shared/assets/icons/books.svg';
import { ReactComponent as InfoIcon } from '../../../shared/assets/icons/icon.svg';
import {
  useConnectRestaurantToPremiumBonus,
  useDeleteDiscoverRestaurantToPremiumBonus,
  useGetDiscoverRestaurantToPremiumBonus,
} from '../lib/premiumBonus';

const h1style = {
  fontSize: '20px',
  fontWeight: 600,
  lineHeight: '24px',
};

const p2style = {
  fontSize: '16px',
  fontWeight: 200,
  lineHeight: '20px',
  color: 'rgba(30, 42, 60, 0.78)',
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    padding: '24px 20px',
    [theme.breakpoints.down(960)]: {
      flexDirection: 'column',
      padding: '16px 16px 24px',
    },
  },
  iconContainer: {
    position: 'relative',
    width: '160px',
    height: '160px',
    padding: '32px 40px',
    borderRadius: '12px',
    border: '1px solid rgba(76, 104, 147, 0.24)',
    [theme.breakpoints.down(960)]: {
      width: '120px',
      height: '120px',
      padding: '20px 28px',
      marginBottom: '16px',
      alignSelf: 'center',
      display: 'flex',
      alignItems: 'center',
    },
  },
  contentContainer: {
    paddingLeft: '20px',
    maxWidth: '720px',
    [theme.breakpoints.down(960)]: {
      paddingLeft: 0,
      maxWidth: '100%',
    },
  },
  title: {
    fontSize: '32px',
    fontWeight: 600,
    lineHeight: '40px',
    color: '#181F28',
    [theme.breakpoints.down(960)]: {
      textAlign: 'center',
    },
  },
  description: {
    ...p2style,
    paddingTop: '8px',
  },
  titleList: {
    ...h1style,
    color: '#181F28',
    paddingTop: '20px',
  },
  listOptions: {
    ...p2style,
    marginTop: 0,
    paddingLeft: '25px',
    '& li': {
      paddingTop: '8px',
      color: 'rgba(30, 42, 60, 0.78)',
    },
    [theme.breakpoints.down(960)]: {
      paddingLeft: '20px',
    },
  },
  link: {
    ...p2style,
    borderBottom: '1px solid rgba(30, 42, 60, 0.78)',
    marginLeft: '25px',
    [theme.breakpoints.down(960)]: {
      marginLeft: 0,
      display: 'inline-block',
    },
  },
  readyTitle: {
    ...p2style,
    color: '#018562',
    marginTop: '32px',
  },
  readyDescription: {
    ...p2style,
    paddingTop: '8px',
    marginBottom: '20px',
    maxWidth: '700px',
  },
  needSettings: {
    maxWidth: '700px',
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#D1E9FF',
    borderRadius: '12px',
    display: 'flex',
    [theme.breakpoints.down(960)]: {
      flexDirection: 'column',
    },
  },
  needSettingsTitle: {
    ...p2style,
    fontWeight: 600,
    color: '#194185',
  },
  needSettingsDescription: {
    ...p2style,
    marginTop: '4px',
    color: '#194185',
  },
  needSettingsIcon: {},
  needSettingsText: {
    paddingLeft: '8px',
    [theme.breakpoints.down(960)]: {
      paddingLeft: 0,
      paddingTop: '8px',
    },
  },
  startPremiumContainer: {
    marginTop: '32px',
    padding: '20px 22px',
    backgroundColor: 'rgba(76, 104, 147, 0.12)',
    borderRadius: '12px',
    maxWidth: '700px',
    [theme.breakpoints.down(960)]: {
      maxWidth: '100%',
      padding: '16px',
    },
  },
  startPremiumH1: {
    ...h1style,
  },
  startPremiumP2: {
    ...p2style,
    marginTop: '8px',
  },
  mt20: {
    marginTop: '20px',
  },
  followButton: {
    marginTop: '20px',
  },
  enableIcon: {
    position: 'absolute',
    left: '-1px',
    top: '-1px',
    width: '90px',
    height: '20px',
    padding: '2px 8px',
    backgroundColor: '#1CBD91',
    borderRadius: '8px 0',
    '& > p': {
      fontSize: '12px',
      fontWeight: 300,
      lineHeight: '16px',
      color: '#FFF',
    },
  },
  disableOption: {
    marginTop: '20px',
  },
  disableButton: {
    marginTop: '16px',
  },
  actionButton: {
    [theme.breakpoints.down(960)]: {
      width: '100%',
      minHeight: 48,
    },
  },
}));

export const LoyaltyPage: FC = () => {
  const classes = useStyles();
  const premiumBonus = useSelector((store: IAppStore) => store.cabinet.premiumBonus);

  const [solidBonusState, setSolidBonusState] = useState<IPremiumBonusType | undefined>();

  useEffect(() => {
    if (!premiumBonus || premiumBonus.indexOf('PENDING_') !== -1) {
      return;
    }

    setSolidBonusState(premiumBonus);
  }, [premiumBonus]);

  useGetDiscoverRestaurantToPremiumBonus(solidBonusState);
  const { mutate: checkConnect } = useConnectRestaurantToPremiumBonus();
  const { mutate: deleteDiscover } = useDeleteDiscoverRestaurantToPremiumBonus();

  const step3 = solidBonusState === 'CONNECTED';
  const step2 = solidBonusState === 'READY_TO_CONNECT';
  const step1 = !step2 && !step3;

  const handleConnectPremiumBonus = () => {
    checkConnect();
  };

  const handleDeleteConnectPremiumBonus = () => {
    deleteDiscover();
  };

  const handleClick = () => {
    window.open('https://premiumbonus.ru/?rs=guestme', '_blank', 'noopener noreferrer');
  };

  return (
    <div className={classes.root}>
      <div className={classes.iconContainer}>
        {step3 && (
          <div className={classes.enableIcon}>
            <Typography>Подключена</Typography>
          </div>
        )}
        <BooksIcon />
      </div>
      <div className={classes.contentContainer}>
        <Typography className={classes.title}>PremiumBonus</Typography>
        <Typography className={classes.description}>
          Эффективная программа лояльности, специально разработанная для&nbsp;ресторанов, которая
          помогает удерживать гостей и&nbsp;увеличивать повторные посещения.
        </Typography>

        {step1 && (
          <div className={classes.needSettings}>
            <div className={classes.needSettingsIcon}>
              <InfoIcon />
            </div>
            <div className={classes.needSettingsText}>
              <Typography className={classes.needSettingsTitle}>
                Сначала настройте программу лояльности
              </Typography>
              <Typography className={classes.needSettingsDescription}>
                PremiumBonus не&nbsp;запущен на&nbsp;вашей кассе. Настройте программу,
                <br />
                а&nbsp;потом вернитесь и&nbsp;активируйте интеграцию.
              </Typography>
            </div>
          </div>
        )}

        <Typography className={classes.titleList}>Как работает интеграция</Typography>
        <ul className={classes.listOptions}>
          <li>Автоматическая активация Premium Bonus при&nbsp;создании/обработке заказа.</li>
          <li>Данные известных гостей подставляются без&nbsp;ручного ввода официантом.</li>
          <li>Карточка гостя с&nbsp;бонусами и&nbsp;статусом лояльности в&nbsp;личном кабинете.</li>
        </ul>
        <a
          className={classes.link}
          href="https://guestme.ru/integrations/premiumbonus/"
          target="_blank"
          rel="noreferrer"
        >
          Подробнее об интеграции
        </a>

        {step1 && (
          <div className={classes.startPremiumContainer}>
            <Typography className={classes.startPremiumH1}>
              Еще не&nbsp;работаете с&nbsp;PremiumBonus?
            </Typography>
            <Typography className={classes.startPremiumP2}>
              Вы можете воспользоваться специальным предложением для&nbsp;клиентов GuestMe.
            </Typography>
            <Typography className={classNames(classes.startPremiumH1, classes.mt20)}>
              AI-ассистент PremiumBonus на&nbsp;три&nbsp;месяца&nbsp;&mdash; бесплатно.
            </Typography>
            <Typography className={classes.startPremiumP2}>
              Вы сможете быстро придумывать тексты для&nbsp;рассылок без&nbsp;копирайтера,
              генерировать подходящие под&nbsp;ваши кампании картинки без&nbsp;дизайнера
              и&nbsp;получать понятные рекомендации, как&nbsp;эффективнее работать с&nbsp;клиентской
              базой и&nbsp;программой лояльности на&nbsp;основе ваших данных.
            </Typography>
            <Typography className={classes.startPremiumP2}>
              Перейдите на&nbsp;сайт, чтобы активировать спец предложение.
            </Typography>
            <Button
              className={classNames(classes.followButton, classes.actionButton)}
              type="submit"
              variant="contained"
              color="primary"
              onClick={handleClick}
            >
              Перейти
            </Button>
          </div>
        )}

        {step2 && (
          <>
            <Typography className={classes.readyTitle}>Готово к подключению!</Typography>
            <Typography className={classes.readyDescription}>
              Ваша программа лояльности PremiumBonus уже работает на&nbsp;кассе. Активируйте
              подключение ниже, чтобы завершить интеграцию.
            </Typography>
            <Button
              className={classes.actionButton}
              type="submit"
              variant="contained"
              color="primary"
              onClick={handleConnectPremiumBonus}
            >
              Подключить
            </Button>
          </>
        )}

        {step3 && (
          <div className={classes.disableOption}>
            <Typography className={classes.startPremiumP2}>
              Вы можете отключить интеграцию с&nbsp;программой лояльности в&nbsp;любое время.
            </Typography>
            <Button
              className={classNames(classes.disableButton, classes.actionButton)}
              type="submit"
              variant="contained"
              color="default"
              onClick={handleDeleteConnectPremiumBonus}
            >
              Отключить
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
